import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { config } from 'config';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  source?: string;
  isSystem?: boolean;
}

export interface ChatResponse {
  message: string;
  error?: string;
}

export const DEFAULT_PRESET_QUESTIONS = [
  'What are the key indications?',
  'Dosage and administration?',
  'Main side effects?',
  'Contraindications?',
  'Key patient counselling points?'
];

const API_BASE = `${config.apiUrl1}treatment-guide`;

@Injectable()
export class TreatmentGuideChatbotService {
  private threadId = '';
  private documentId: number | null = null;

  constructor(private http: HttpClient) {}

  startConversation(documentId: number): Observable<boolean> {
    this.documentId = documentId;
    return this.createThread();
  }

  sendMessage(question: string): Observable<ChatResponse> {
    if (!this.threadId || this.documentId == null) {
      return of({ message: '', error: 'Conversation not started.' });
    }

    return this.http.post<any>(`${API_BASE}/message`, {
      thread_id: this.threadId,
      document_id: this.documentId,
      message: question
    }).pipe(
      map(response => ({ message: response.message || 'No response received.' })),
      catchError(err => of({
        message: '',
        error: err?.error?.error || err?.error?.message || 'Failed to get AI response.'
      }))
    );
  }

  generatePresetQuestions(): Observable<string[]> {
    if (!this.threadId || this.documentId == null) return of(DEFAULT_PRESET_QUESTIONS);

    return this.http.post<any>(`${API_BASE}/preset-questions`, {
      thread_id: this.threadId,
      document_id: this.documentId
    }).pipe(
      map(response => {
        if (response.has_preset === false) {
          return [];
        }
        if (Array.isArray(response.questions) && response.questions.length === 5) {
          return response.questions;
        }
        return DEFAULT_PRESET_QUESTIONS;
      }),
      catchError(() => of(DEFAULT_PRESET_QUESTIONS))
    );
  }

  resetConversation(): void {
    this.threadId = '';
  }

  private createThread(): Observable<boolean> {
    return this.http.post<any>(`${API_BASE}/thread`, {}).pipe(
      map(response => {
        this.threadId = response.thread_id;
        return !!this.threadId;
      }),
      catchError(() => of(false))
    );
  }
}
